import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import { Typography, TextField, InputAdornment, IconButton, Tooltip, Grid } from "@material-ui/core";
import {
	Page,
	PageContent,
	ScrollToTopOnMount,
	Pagination,
	PaginationHeader,
	PageHeaderListActions,
} from "frame/components";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";
import HazardousSubstanceCard from "../cards/hazardous-substance-card";
import {
	EditHazardousSubstanceForm,
	PageHeaderTabs,
} from "features/health-safety-forms";
import { getPageResults } from "utils";
import * as fromHealthSafetyForm from "features/health-safety-forms";

const useStyles = makeStyles((theme) => ({
	root: {},
	filter: { display: "flex" },
	search: {
		display: "flex"
	},
	gridContainer: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(3),
		[theme.breakpoints.down("xs")]: {
			marginTop: theme.spacing(3)
		}
	},
	filterGrid: {
		paddingTop: theme.spacing(1)
	},
}));

const HazardousSubstanceList = ({
	role,
	hazardousSubstances,
	onDelete,
	onGetHazardousSubstanceList,
}) => {
	const classes = useStyles();
	const [viewId, setViewId] = useState();
	const [editId, setEditId] = useState();
	const [searchFilter, setSearchFilter] = useState("");
	const [showFilters, setShowFilters] = useState(false);
	const isInitialMount = useRef(true);

	const hazardousSubstanceList = [...getPageResults(hazardousSubstances)].sort((a, b) => {
		const aActive = String(a.status).toLowerCase() === "active";
		const bActive = String(b.status).toLowerCase() === "active";
		return (bActive ? 1 : 0) - (aActive ? 1 : 0);
	});
	const empty = hazardousSubstanceList && hazardousSubstanceList.length === 0;

	const onView = (id) => () => setViewId(id);

	const onEdit = (id) => () => setEditId(id);

	const onCancelEdit = () => {
		setEditId(null);
		setViewId(null);
	};

	// Debounced search effect - triggers search after user stops typing
	useEffect(() => {
		// Skip debounce on initial mount
		if (isInitialMount.current) {
			isInitialMount.current = false;
			return;
		}

		const searchTimeout = setTimeout(() => {
			onGetHazardousSubstanceList({
				page: 1,
				pageSize: 20,
				orderBy: "createdAt",
				ascending: false,
				searchKey: searchFilter
			});
		}, 500); // 500ms debounce

		return () => clearTimeout(searchTimeout);
	}, [searchFilter, onGetHazardousSubstanceList]);

	return (
		<Page className={classes.root} title="Hazardous Substance Library">
			<ScrollToTopOnMount />
							<PageHeaderTabs
					// breadcrumbs="Hazardous Substances"
					title="Hazardous Substance Library"
					// addTabTitle="New"
					// listTabTitle="See All"
					addTabPath={fromHealthSafetyForm.ADD_HAZARDOUS_SUBSTANCE_PATH}
					hideListTabButton={true}
					staffUserHide={true}
					backActionPath={fromHealthSafetyForm.INFO_ROUTE}
				
						titleAdornment={
						<PageHeaderListActions
							showFilters={showFilters}
							onToggleFilters={() => setShowFilters(!showFilters)}
						/>
					}>
					{/* START: Search */}
					{showFilters && (
						<div className={classes.gridContainer}>
							<Grid container spacing={2} className={classes.filterGrid}>
								<Grid item xs={12} md={6}>
									<TextField
										className={classes.search}
										id="input-with-icon-textfield"
										variant="outlined"
										label="Search"
										InputProps={{
											startAdornment: (
												<InputAdornment position="start">
													<SearchIcon />
												</InputAdornment>
											),
											endAdornment: searchFilter && (
												<InputAdornment position="end">
													<IconButton
														aria-label="clear search"
														onClick={() => {
															setSearchFilter("");
															onGetHazardousSubstanceList({
																page: 1,
																pageSize: 20,
																orderBy: "createdAt",
																ascending: false,
																searchKey: ""
															});
														}}
														edge="end"
														size="small"
													>
														<ClearIcon />
													</IconButton>
												</InputAdornment>
											)
										}}
										value={searchFilter}
										onChange={(val) => {
											const { value } = val.target;
											setSearchFilter(value);
											// Real-time search with debounce effect will handle the API call
										}}
										style={{ backgroundColor: "white", maxWidth: 480, width: "100%" }}
										placeholder={"e.g. Name"}
									/>
								</Grid>
							</Grid>
						</div>
					)}
					{/* END: Search */}
				</PageHeaderTabs>
			<PageContent pad>
				<PaginationHeader
					hidePagination
					pagination={hazardousSubstances}
				/>

				{!empty &&
					hazardousSubstanceList.map((hazardousSubstance, index) => (
						<HazardousSubstanceCard
							key={index}
							hazardousSubstance={hazardousSubstance}
							role={role}
							onView={onView(hazardousSubstance.id)}
							onEdit={onEdit(hazardousSubstance.id)}
							onDelete={onDelete}
						/>
					))}

				{empty && (
					<Typography variant="body1">
						There are currently no Hazardous Substances listed.
					</Typography>
				)}

				<Pagination
					hidePagination
					onGetList={onGetHazardousSubstanceList}
					pagination={hazardousSubstances}
					orderBy="createdAt"
					ascending={false}
					searchKey={searchFilter}
				/>
			</PageContent>

			{viewId && (
				<EditHazardousSubstanceForm
					readOnly
					id={viewId}
					onClose={onCancelEdit}
				/>
			)}
			{editId && (
				<EditHazardousSubstanceForm
					id={editId}
					onClose={onCancelEdit}
				/>
			)}
		</Page>
	);
};

HazardousSubstanceList.propTypes = {
	role: PropTypes.string.isRequired,
	hazardousSubstances: PropTypes.object.isRequired,
	onDelete: PropTypes.func.isRequired,
	onGetHazardousSubstanceList: PropTypes.func.isRequired,
};

export default HazardousSubstanceList;
